import { useState } from "react";
import { toast } from "sonner";
import { Cpu, Loader2, Pencil, Save, Search, X, ScanSearch } from "lucide-react";
import { MAC_APPS, searchMac } from "@/lib/macLookup";
import { MacMatch } from "@/components/clients/MacLookupBox";
import { api } from "@/lib/api";
import { InfoCard } from "./InfoBits";


const fmtMac = (v) => {
  const h = (v || "").replace(/[^0-9a-fA-F]/g, "").toUpperCase().slice(0, 12);
  return h.match(/.{1,2}/g)?.join(":") || "";
};
const validMac = (v) => !v || /^([0-9A-F]{2}:){5}[0-9A-F]{2}$/.test(v);

export const MacCard = ({ c, onSaved }) => {
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({});
  const [more, setMore] = useState({});
  const [busy, setBusy] = useState(false);
  const macs = c.macs || {};
  const extras = c.macs_more || {};
  const start = () => { setForm({ ...macs }); setMore(JSON.parse(JSON.stringify(extras))); setEdit(true); };
  const dropExtra = (k, m) => setMore({ ...more, [k]: (more[k] || []).filter((x) => x !== m) });
  const bad = MAC_APPS.filter(([k]) => !validMac(form[k]));
  const [checking, setChecking] = useState("");
  const [found, setFound] = useState(null);
  const check = async (k) => {
    setChecking(k); setFound(null);
    try {
      const r = await searchMac(macs[k], k);
      setFound({ app: k, matches: r.matches, error: r.errors[k] });
    } catch (e) {
      setFound({ app: k, error: e.response?.data?.detail || e.message, matches: [] });
    }
    setChecking("");
  };

  const [fetching, setFetching] = useState(false);
  const fetchPanels = async () => {
    setFetching(true);
    try {
      const { data } = await api.post(`/clients/${c.id}/macs/fetch`);
      const fails = Object.keys(data.errors || {}).length;
      if (data.added) { toast.success(`${data.added} MAC(s) encontrado(s) e salvo(s)`); onSaved(); }
      else if (data.found.length) toast.info("Os MACs encontrados já estão na ficha");
      else toast.warning(`Usuário ${c.panel_user} não encontrado nos painéis${fails ? ` (${fails} painel(is) sem resposta)` : ""}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao buscar nos painéis"); }
    setFetching(false);
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/clients/${c.id}/macs`, { macs: form, more });
      toast.success("MACs salvos");
      setEdit(false);
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao salvar MACs"); }
    setBusy(false);
  };

  return (
    <InfoCard testid="card-macs" title="Dispositivos (MAC)" icon={Cpu} color="#F472B6">
      {MAC_APPS.map(([k, label]) => (
        <div key={k} className="min-w-0">
          <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
          {edit ? (
            <input data-testid={`mac-input-${k}`} value={form[k] || ""} placeholder="00:00:00:00:00:00"
              onChange={(e) => setForm({ ...form, [k]: fmtMac(e.target.value) })}
              className={`mt-0.5 w-full rounded-md border bg-[#0B1019] px-2 py-1 font-mono text-sm text-white outline-none focus:border-sky-500 ${validMac(form[k]) ? "border-[#202D45]" : "border-red-500"}`} />
          ) : (
            <div className="mt-0.5 flex items-center gap-1.5">
              <span data-testid={`mac-value-${k}`} className={`font-mono text-sm ${macs[k] ? "text-white" : "text-slate-600"}`}>{macs[k] || "—"}</span>
              {macs[k] && (
                <button data-testid={`mac-search-${k}`} title="Buscar no painel do app" disabled={!!checking}
                  onClick={() => check(k)} className="rounded p-1 text-pink-300 transition-colors hover:bg-pink-500/15 disabled:opacity-40">
                  {checking === k ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />}
                </button>
              )}
            </div>
          )}
          {((edit ? more[k] : extras[k]) || []).map((m) => (
            <div key={m} data-testid={`mac-extra-${k}-${m.replace(/:/g, "")}`} className="mt-0.5 flex items-center gap-1 font-mono text-[11px] text-slate-400">
              + {m}
              {edit && (
                <button data-testid={`mac-extra-remove-${k}-${m.replace(/:/g, "")}`} onClick={() => dropExtra(k, m)}
                  className="rounded p-0.5 text-red-300 hover:bg-red-500/15"><X className="h-3 w-3" /></button>
              )}
            </div>
          ))}
        </div>
      ))}
      {found && (
        <div data-testid="mac-card-result" className="col-span-2 space-y-1.5">
          {found.error && <div className="text-xs text-red-300">{found.error}</div>}
          {!found.error && !found.matches.length && <div className="text-xs text-slate-500">MAC não encontrado no painel.</div>}
          {found.matches.map((m, i) => <MacMatch key={i} app={found.app} m={m} />)}
        </div>
      )}
      <div className="col-span-2 mt-1 flex gap-2">
        {edit ? (
          <>
            <button data-testid="mac-save-btn" disabled={busy || bad.length > 0} onClick={save}
              className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-pink-500 px-3 py-2 text-xs font-bold text-slate-950 transition-opacity hover:opacity-90 disabled:opacity-40">
              <Save className="h-4 w-4" /> {bad.length ? "MAC incompleto" : "Salvar MACs"}
            </button>
            <button data-testid="mac-cancel-btn" onClick={() => setEdit(false)}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-[#202D45] px-3 py-2 text-xs font-semibold text-slate-300 transition-colors hover:bg-[#1A2234]">
              <X className="h-4 w-4" /> Cancelar
            </button>
          </>
        ) : (
          <>
          <button data-testid="mac-fetch-panels-btn" onClick={fetchPanels} disabled={fetching}
            className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-pink-500 px-3 py-2 text-xs font-bold text-slate-950 transition-opacity hover:opacity-90 disabled:opacity-50">
            {fetching ? <Loader2 className="h-4 w-4 animate-spin" /> : <ScanSearch className="h-4 w-4" />} {fetching ? "Buscando nos 7 painéis..." : "Buscar MAC nos painéis"}
          </button>
          <button data-testid="mac-edit-btn" onClick={start}
            className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg border border-pink-500/40 bg-pink-500/10 px-3 py-2 text-xs font-bold text-pink-300 transition-colors hover:bg-pink-500/20">
            <Pencil className="h-4 w-4" /> Editar MACs
          </button>
          </>
        )}
      </div>
    </InfoCard>
  );
};
